import { ButtonHTMLAttributes, CSSProperties, PropsWithChildren } from 'react';

import clsx from 'clsx';

import { IconButton } from '../Button';
import { Flex } from '../Flex';
import { IconXLine } from '../Icon';
import { Text } from '../Text';
import { chip, chipWithIconGap, prefixIconWrapper } from './Chip.css';
import { ChipSize, ChipType } from './types';
import { chipSelectedTextSize, chipTextSize } from './utils';

export interface ChipProps extends PropsWithChildren<
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'prefix'>
> {
  chipType?: ChipType;
  chipSize?: ChipSize;
  selected?: boolean;
  disabled?: boolean;
  /** 칩 시작 위치에 표시할 아이콘 등 */
  prefix?: React.ReactNode;
  /** 칩이 아이콘만 표시할지 여부 */
  icon?: React.ReactNode;
  /** 닫기 버튼 표시 여부 */
  closable?: boolean;
  /** 닫기 버튼 클릭 핸들러 */
  onClose?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /** 칩 너비 */
  width?: CSSProperties['width'];
}

export const Chip: React.FC<ChipProps> = ({
  chipType = 'normal',
  chipSize = 'm',
  selected = false,
  disabled = false,
  prefix,
  icon,
  closable = false,
  onClose,
  children,
  type = 'button',
  className,
  width = 'fit-content',
  style,
  ...props
}) => {
  const handleCloseClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onClose?.(e);
  };

  const closeButtonSize =
    chipSize === 'xs' || chipSize === 's' ? 'xs' : chipSize === 'm' ? 's' : 'm';
  const iconOnly = Boolean(icon) && !children && !closable && !Boolean(prefix);

  return (
    <button
      className={clsx(
        chip({
          chipType,
          chipSize,
          selected,
          disabled,
          iconOnly,
          hasIcon: Boolean(prefix),
          closable,
        }),
        chipWithIconGap({ chipSize }),
        className,
      )}
      type={type}
      disabled={disabled}
      style={iconOnly ? undefined : { width, ...style }}
      {...props}
    >
      <Flex
        justify="center"
        align="center"
        className={clsx(chipWithIconGap({ chipSize }), prefixIconWrapper)}
        style={{ minWidth: 0 }}
      >
        {prefix ?? null}
        {icon ?? null}
        {!iconOnly && typeof children === 'string' ? (
          <Text
            textStyle={selected ? chipSelectedTextSize(chipSize) : chipTextSize(chipSize)}
            ellipsis
            showTooltipOnEllipsis
          >
            {children}
          </Text>
        ) : !iconOnly ? (
          children
        ) : null}
      </Flex>
      {closable && !iconOnly && (
        <IconButton
          buttonType="ghost"
          buttonSize={closeButtonSize}
          style={{ borderRadius: 100 }}
          onClick={handleCloseClick}
          disabled={disabled}
          icon={<IconXLine />}
          tooltipMessage="닫기"
        />
      )}
    </button>
  );
};
